import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { VStack, HStack, Text, Box, Pressable, Image } from 'native-base';
import { useTranslation } from 'react-i18next';

interface Props {
  images: string[];
  close: () => void;
}

export const PodModal = ({ images, close }: Props) => {
  const { t } = useTranslation();
  const downloadImage = (base64Data: string, index: number) => {
    try {
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = base64Data;
      link.download = `pod-image-${index + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Calculate modal content width based on number of images
  const CARD_WIDTH_PX = 280; // matches flex basis of each image card
  const CARD_GAP_PX = 12; // matches gap between cards
  const HORIZONTAL_PADDING_PX = 40; // modal inner padding: 20 left + 20 right
  const MIN_WIDTH_PX = 360; // ensure reasonable minimum width
  const imagesCount = Array.isArray(images) ? images.length : 0;
  const computedContentWidthPx = Math.max(
    MIN_WIDTH_PX,
    imagesCount > 0
      ? imagesCount * CARD_WIDTH_PX + Math.max(0, imagesCount - 1) * CARD_GAP_PX + HORIZONTAL_PADDING_PX
      : MIN_WIDTH_PX,
  );
  const clampedContentWidthPx = Math.min(
    computedContentWidthPx,
    Math.floor(window.innerWidth * 0.9),
  );

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        margin: 0,
        padding: 0,
      }}
      onClick={close}>
      <div
        style={{
          backgroundColor: '#ffffff',
          padding: '20px',
          borderRadius: '10px',
          width: `${clampedContentWidthPx}px`,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.3)',
          position: 'relative',
          zIndex: 10000,
        }}
        onClick={e => e.stopPropagation()}>
        <VStack space={4}>
          <HStack justifyContent="space-between" alignItems="center">
            <Text fontSize="18px" fontFamily="Segoe UI" bold color="#293D8B">
              {t('pod_title')}
            </Text>
            <button
              onClick={close}
              style={{
                backgroundColor: '#f5f5f5',
                border: 'none',
                padding: '8px',
                borderRadius: '5px',
                cursor: 'pointer',
              }}>
              <Text fontSize="16px" color="#666">
                ✕
              </Text>
            </button>
          </HStack>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              alignItems: 'flex-start',
            }}>
            {images.map((imageData, index) => (
              <div
                key={index}
                style={{
                  flex: '0 1 280px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '12px',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff',
                }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                  }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {t('pod_image_label', { index: index + 1 })}
                  </span>
                  <button
                    onClick={() => downloadImage(imageData, index)}
                    style={{
                      backgroundColor: '#4c66fb',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}>
                    <HStack space={1} alignItems="center">
                      <Image
                        source={{
                          uri: `${process.env.PUBLIC_URL}/icons/download.svg`,
                        }}
                        alt="download"
                        width={4}
                        height={4}
                        style={{ filter: 'brightness(0) invert(1)' }}
                      />
                      <Text fontSize="12px" color="white" fontFamily="Segoe UI">
                        {t('download')}
                      </Text>
                    </HStack>
                  </button>
                </div>
                <div
                  style={{
                    width: '100%',
                    maxHeight: '320px',
                    overflow: 'hidden',
                    borderRadius: '5px',
                  }}>
                  <img
                    src={imageData}
                    alt={`POD Image ${index + 1}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '320px',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </VStack>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
